<script lang="ts">
declare module "@tanstack/vue-table" {
	interface FilterMeta {
		itemRank?: RankingInfo;
	}

	interface ColumnMeta<TData extends RowData, TValue> {
		class?: any;
		style?: CSSProperties;
		csv?: boolean | ((ctx: CellContext<TData, TValue>) => unknown);
		hidden?: boolean;
		shrink?: boolean;
	}
}

export type DataTableOptions<TData> = Partial<
	Omit<TableOptions<TData>, "state" | "columns" | "data">
>;

export interface DataTableRootProps<TData extends RowData> extends ClassProps {
	data: TData[];
	options?: DataTableOptions<TData>;
	loading?: boolean;
	selectionMode?: SelectionMode;
	stateStorage?: StorageLocation;

	pagination?: PaginationState;
	filter?: string;
	fieldFilters?: ColumnFiltersState;
	selection?: RowSelectionState;
	sorting?: SortingState;

	manualPagination?: boolean;
	getRowId?: DataCollectionOptions["getRowId"];

	/**
	 * @default true
	 */
	styled?: boolean;
}

export type DataTableRootEmits = {
	"update:sorting": [value: SortingState];
	"update:filter": [value: string];
	"update:fieldFilters": [value: ColumnFiltersState];
	"update:selection": [value: RowSelectionState];
	"update:pagination": [value: PaginationState];
};

export type DataTableRow<TData> = {
	row: Row<TData>;
	style?: CSSProperties;
};

export interface DataTableRootContext<TData extends RowData> {
	styled: Ref<boolean>;
	columns: ShallowRef<ColumnDefResolved<TData>[]>;
	ready: Ref<boolean>;
	loading: Ref<boolean>;
	pagination: Ref<boolean>;
	virtual: Ref<{
		enabled: boolean;
		totalSize: number;
	}>;
}

export const [injectDataTableRootContext, provideDataTableRootContext] =
	createContext<DataTableRootContext<any>>("DataTableRoot");
</script>

<script setup lang="ts" generic="TData extends RowData = RowData">
import type { RankingInfo } from "@tanstack/match-sorter-utils";
import type {
	ColumnDefResolved,
	ColumnFiltersState,
	PaginationState,
	Row,
	RowSelectionState,
	TableOptions,
} from "@tanstack/vue-table";
import { type CellContext, type RowData, type SortingState } from "@tanstack/vue-table";
import { createContext } from "reka-ui";
import { ref, shallowRef, toRef, type CSSProperties, type Ref, type ShallowRef } from "vue";
import type { ClassProps } from "../../lib/base-props";
import {
	useDataCollection,
	type DataCollectionOptions,
} from "../data-collection/composables/data-collection";
import { generateCSV } from "./data-table-csv";
import { useStateModel } from "./state/state-model";
import type { StorageLocation } from "./state/types";

const props = withDefaults(defineProps<DataTableRootProps<TData>>(), {
	styled: true,
});
const emit = defineEmits<DataTableRootEmits>();

const columns = shallowRef<ColumnDefResolved<TData>[]>([]);

/* eslint vue/no-dupe-keys: "off"*/
const pagination = ref(false);

const ready = ref(false);
const loading = toRef(props, "loading");

const virtual = ref({
	enabled: false,
	totalSize: 0,
});

const { manager, state } = useDataCollection(
	() => props.data,
	columns,
	() => ({
		pagination: pagination.value,
		storage: props.stateStorage,
		manualPagination: props.manualPagination,
		getRowId: props.getRowId,
		...props.options,
	})
);

useStateModel("sorting", props, emit, state);
useStateModel("filter", props, emit, state);
useStateModel("fieldFilters", props, emit, state);
useStateModel("pagination", props, emit, state);
useStateModel("selection", props, emit, state);

provideDataTableRootContext({
	columns,
	ready,
	loading,
	pagination,
	virtual,
	styled: toRef(props, "styled"),
});

defineExpose({
	table: manager,
	generateCSV: () => {
		return generateCSV(manager);
	},
});
</script>

<template>
	<slot />
</template>
